// ResponsiveDatePicker.tsx

import React, {useMemo, useState} from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {IconButton, Link, Popover, Stack, useMediaQuery, useTheme} from '@mui/material';
import {DateCalendar} from '@mui/x-date-pickers/DateCalendar';
import {PrimitiveAtom, useAtom} from 'jotai';
import dayjs, {Dayjs} from 'dayjs';

export interface ResponsiveDatePickerProps {
   dateAtom: PrimitiveAtom<Dayjs>;
   disabled?: boolean;
}

import {makeStyles} from 'tss-react/mui';

const useStyles = makeStyles({name: 'ResponsiveDatePickerStyles'})(() => ({
   disabledLinkStyle: {
      '&[disabled]': {
         cursor: 'inherit',
      },
   },
}));
export default function ResponsiveDatePicker({dateAtom, disabled = false}: ResponsiveDatePickerProps) {
   const {classes} = useStyles();
   const [date, setDate] = useAtom(dateAtom);

   const theme = useTheme();

   const lgUp = useMediaQuery(theme.breakpoints.up('lg'));

   const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

   // Always show a date — long format ("Tuesday, August 26, 2026") once
   // there's room, short format ("8/26/2026") otherwise. Previously
   // this went blank entirely below `md`, removing the date link (just
   // the calendar icon remained) — no longer needed on TopNav now that
   // it falls back to a 2nd row instead of running out of room (Duane,
   // 2026-08-26), and there's no reason for BottomNav (the other
   // consumer of this component) to hide it either.
   const formattedDate = useMemo(() => {
      if (lgUp) {
         return date.format('dddd, MMMM D, YYYY');
      }

      return date.format('M/D/YYYY');
   }, [date, lgUp]);

   const open = Boolean(anchorEl);

   const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) {
         return;
      }

      setAnchorEl(event.currentTarget);
   };

   const handleClose = () => {
      setAnchorEl(null);
   };

   const handleChange = (value: Dayjs | null) => {
      if (!value) {
         return;
      }

      setDate(value);
      handleClose();
   };

   return (
      <>
         <Stack direction='row' spacing={0.25} sx={{alignItems: 'center'}}>
            <Link
               component='button'
               type='button'
               underline={!disabled ? 'hover' : 'none'}
               color='inherit'
               className={classes.disabledLinkStyle}
               onClick={handleOpen}
               disabled={disabled}
               sx={{
                  whiteSpace: 'nowrap',
                  typography: 'body2',
               }}
            >
               {formattedDate}
            </Link>

            {!disabled && (
               <IconButton size='small' onClick={handleOpen}>
                  <CalendarMonthIcon fontSize='small' />
               </IconButton>
            )}
         </Stack>

         <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
               vertical: 'bottom',
               horizontal: 'left',
            }}
            transformOrigin={{
               vertical: 'top',
               horizontal: 'left',
            }}
         >
            <DateCalendar value={date} onChange={handleChange} maxDate={dayjs()} />
         </Popover>
      </>
   );
}
